import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './lib/AuthContext'
import ErrorBoundary from './components/ErrorBoundary'
import Login from './pages/Login'
import ResetPassword from './pages/ResetPassword'
import SetPassword from './pages/SetPassword'
import Tracking from './pages/Tracking'
import QuieroSerPortero from './pages/QuieroSerPortero'
import AdminDashboard from './pages/admin/Dashboard'
import AdminEntregas from './pages/admin/Entregas'
import AdminClientes from './pages/admin/Clientes'
import AdminClienteFueraZona from './pages/admin/ClienteFueraZona'
import AdminCobros from './pages/admin/Cobros'
import AdminPedidos from './pages/admin/Pedidos'
import AdminBarrios from './pages/admin/Barrios'
import AdminRecordatorios from './pages/admin/Recordatorios'
import AdminEtiquetas from './pages/admin/Etiquetas'
import AdminArmado from './pages/admin/Armado'
import AdminStock from './pages/admin/Stock'
import AdminProducto from './pages/admin/Producto'
import AdminKits from './pages/admin/Kits'
import AdminKit from './pages/admin/Kit'
import AdminProveedores from './pages/admin/Proveedores'
import AdminOrdenCompra from './pages/admin/OrdenCompra'
import AdminUsuarios from './pages/admin/Usuarios'
import AdminReportes from './pages/admin/Reportes'
import AdminModeloFinanciero from './pages/admin/ModeloFinanciero'
import Invitar from './pages/Invitar'
import AdminRentProducto from './pages/admin/RentabilidadProducto'
import AdminRentBarrio from './pages/admin/RentabilidadBarrio'
import AdminComisionesMP from './pages/admin/ComisionesMP'
import AdminMrrChurn from './pages/admin/MrrChurn'
import AdminCohortLtv from './pages/admin/CohortLtv'
import AdminVecinos from './pages/admin/Vecinos'
import AdminIva from './pages/admin/Iva'
import AdminStockTurnover from './pages/admin/StockTurnover'
import AdminConfiguracionIA from './pages/admin/ConfiguracionIA'
import AdminPostulacionesPorteros from './pages/admin/PostulacionesPorteros'
import AdminCalendarioMarketing from './pages/admin/CalendarioMarketing'
import AdminMarketingVolante from './pages/admin/MarketingVolante'
import AdminMarketingAgent from './pages/admin/MarketingAgent'
import AdminCompras from './pages/admin/Compras'
import AdminMateriales from './pages/admin/Materiales'
import RepartidorHoy from './pages/repartidor/Hoy'
import PorteroDashboard from './pages/portero/Dashboard'
import ClienteDashboard from './pages/cliente/Dashboard'
import ClienteInicio from './pages/cliente/Inicio'
import ClienteMiPlan from './pages/cliente/MiPlan'
import ClientePedidos from './pages/cliente/Pedidos'
import ClienteVecinos from './pages/cliente/Vecinos'
import ClienteSoporte from './pages/cliente/Soporte'
import ClienteCuenta from './pages/cliente/Cuenta'
import ClientePerfil from './pages/cliente/Perfil'
import ClienteNuevaSuscripcion from './pages/cliente/NuevaSuscripcion'
import ClienteProbarKit from './pages/cliente/ProbarKit'
import ClienteTienda from './pages/cliente/Tienda'
import ClienteCargarTarjeta from './pages/cliente/CargarTarjeta'
import ClientePagarPedido from './pages/cliente/PagarPedido'
import RequirePerfilCompleto from './components/RequirePerfilCompleto'
import BetaGate from './components/BetaGate'

function RequireAuth({ children, allow }) {
  const { user, role, loading } = useAuth()
  const location = useLocation()
  if (loading) return <div className="loading">Cargando...</div>
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />
  if (allow && !allow.includes(role)) return <Navigate to="/" replace />
  return children
}

function RoleHome() {
  const { user, role, loading } = useAuth()
  // Si el dominio es tienda.limpack.uy y no hay user, ir directo a la tienda pública
  const esSubdominioTienda = typeof window !== 'undefined' && window.location.hostname === 'tienda.limpack.uy'
  if (loading) return <div className="loading">Cargando...</div>
  if (!user) return <Navigate to={esSubdominioTienda ? '/tienda' : '/login'} replace />
  if (role === 'admin') return <Navigate to="/admin" replace />
  if (role === 'repartidor') return <Navigate to="/repartidor/hoy" replace />
  if (role === 'portero') return <Navigate to="/portero" replace />
  if (role === 'cliente') return <Navigate to={esSubdominioTienda ? '/tienda' : '/cliente'} replace />
  return <div className="loading">Sin rol asignado. Contactá al admin.</div>
}

export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/invitar/:token" element={<Invitar />} />
          <Route path="/invitar" element={<Invitar />} />
          <Route path="/d/:token" element={<Tracking />} />
          <Route path="/quiero-ser-portero" element={<QuieroSerPortero />} />
          {/* Rutas públicas — visitantes pueden navegar sin login (con BetaGate mientras está cerrado) */}
          <Route path="/tienda" element={<BetaGate origen="tienda"><ClienteTienda /></BetaGate>} />
          <Route path="/suscribirme" element={<BetaGate origen="suscribirme"><ClienteNuevaSuscripcion /></BetaGate>} />
          <Route path="/" element={<RoleHome />} />
          <Route path="/admin" element={<RequireAuth allow={['admin']}><AdminDashboard /></RequireAuth>} />
          <Route path="/admin/entregas" element={<RequireAuth allow={['admin','repartidor']}><AdminEntregas /></RequireAuth>} />
          <Route path="/admin/clientes" element={<RequireAuth allow={['admin']}><AdminClientes /></RequireAuth>} />
          <Route path="/admin/clientes/fuera-zona" element={<RequireAuth allow={['admin']}><AdminClienteFueraZona /></RequireAuth>} />
          <Route path="/admin/cobros" element={<RequireAuth allow={['admin']}><AdminCobros /></RequireAuth>} />
          <Route path="/admin/pedidos" element={<RequireAuth allow={['admin']}><AdminPedidos /></RequireAuth>} />
          <Route path="/admin/barrios" element={<RequireAuth allow={['admin']}><AdminBarrios /></RequireAuth>} />
          <Route path="/admin/recordatorios" element={<RequireAuth allow={['admin']}><AdminRecordatorios /></RequireAuth>} />
          <Route path="/admin/etiquetas" element={<RequireAuth allow={['admin','repartidor']}><AdminEtiquetas /></RequireAuth>} />
          <Route path="/admin/armado" element={<RequireAuth allow={['admin','repartidor']}><AdminArmado /></RequireAuth>} />
          <Route path="/admin/stock" element={<RequireAuth allow={['admin']}><AdminStock /></RequireAuth>} />
          <Route path="/admin/stock/:sku" element={<RequireAuth allow={['admin']}><AdminProducto /></RequireAuth>} />
          <Route path="/admin/kits" element={<RequireAuth allow={['admin']}><AdminKits /></RequireAuth>} />
          <Route path="/admin/kits/:codigo" element={<RequireAuth allow={['admin']}><AdminKit /></RequireAuth>} />
          <Route path="/admin/materiales" element={<RequireAuth allow={['admin']}><AdminMateriales /></RequireAuth>} />
          <Route path="/admin/proveedores" element={<RequireAuth allow={['admin']}><AdminProveedores /></RequireAuth>} />
          <Route path="/admin/orden-compra" element={<RequireAuth allow={['admin']}><AdminOrdenCompra /></RequireAuth>} />
          <Route path="/admin/usuarios" element={<RequireAuth allow={['admin']}><AdminUsuarios /></RequireAuth>} />
          <Route path="/admin/reportes" element={<RequireAuth allow={['admin']}><AdminReportes /></RequireAuth>} />
          <Route path="/admin/reportes/modelo-financiero" element={<RequireAuth allow={['admin']}><AdminModeloFinanciero /></RequireAuth>} />
          <Route path="/admin/reportes/rentabilidad-producto" element={<RequireAuth allow={['admin']}><AdminRentProducto /></RequireAuth>} />
          <Route path="/admin/reportes/rentabilidad-barrio" element={<RequireAuth allow={['admin']}><AdminRentBarrio /></RequireAuth>} />
          <Route path="/admin/reportes/comisiones-mp" element={<RequireAuth allow={['admin']}><AdminComisionesMP /></RequireAuth>} />
          <Route path="/admin/reportes/mrr-churn" element={<RequireAuth allow={['admin']}><AdminMrrChurn /></RequireAuth>} />
          <Route path="/admin/reportes/cohort-ltv" element={<RequireAuth allow={['admin']}><AdminCohortLtv /></RequireAuth>} />
          <Route path="/admin/reportes/vecinos" element={<RequireAuth allow={['admin']}><AdminVecinos /></RequireAuth>} />
          <Route path="/admin/reportes/iva" element={<RequireAuth allow={['admin']}><AdminIva /></RequireAuth>} />
          <Route path="/admin/reportes/stock-turnover" element={<RequireAuth allow={['admin']}><AdminStockTurnover /></RequireAuth>} />
          <Route path="/admin/configuracion-ia" element={<RequireAuth allow={['admin']}><AdminConfiguracionIA /></RequireAuth>} />
          <Route path="/admin/postulaciones-porteros" element={<RequireAuth allow={['admin']}><AdminPostulacionesPorteros /></RequireAuth>} />
          <Route path="/admin/calendario-marketing" element={<RequireAuth allow={['admin']}><AdminCalendarioMarketing /></RequireAuth>} />
          <Route path="/admin/marketing/volante" element={<RequireAuth allow={['admin']}><AdminMarketingVolante /></RequireAuth>} />
          <Route path="/admin/marketing-agent" element={<RequireAuth allow={['admin','marketing']}><AdminMarketingAgent /></RequireAuth>} />
          <Route path="/admin/compras" element={<RequireAuth allow={['admin']}><AdminCompras /></RequireAuth>} />
          <Route path="/repartidor/hoy" element={<RequireAuth allow={['admin','repartidor']}><RepartidorHoy /></RequireAuth>} />
          <Route path="/portero" element={<RequireAuth allow={['portero','admin']}><PorteroDashboard /></RequireAuth>} />
          <Route path="/cliente" element={<RequireAuth allow={['cliente','admin']}><ClienteInicio /></RequireAuth>} />
          <Route path="/cliente/mi-plan" element={<RequireAuth allow={['cliente','admin']}><ClienteMiPlan /></RequireAuth>} />
          <Route path="/cliente/pedidos" element={<RequireAuth allow={['cliente','admin']}><ClientePedidos /></RequireAuth>} />
          <Route path="/cliente/vecinos" element={<RequireAuth allow={['cliente','admin']}><ClienteVecinos /></RequireAuth>} />
          <Route path="/cliente/soporte" element={<RequireAuth allow={['cliente','admin']}><ClienteSoporte /></RequireAuth>} />
          <Route path="/cliente/cuenta" element={<RequireAuth allow={['cliente','admin']}><ClienteCuenta /></RequireAuth>} />
          <Route path="/cliente/dashboard-old" element={<RequireAuth allow={['cliente','admin']}><ClienteDashboard /></RequireAuth>} />
          <Route path="/cliente/perfil" element={<RequireAuth allow={['cliente','admin']}><ClientePerfil /></RequireAuth>} />
          <Route path="/cliente/nueva-suscripcion" element={<RequireAuth allow={['cliente','admin']}><ClienteNuevaSuscripcion /></RequireAuth>} />
          <Route path="/cliente/probar-kit" element={<RequireAuth allow={['cliente','admin']}><ClienteProbarKit /></RequireAuth>} />
          <Route path="/cliente/tienda" element={<RequireAuth allow={['cliente','admin']}><ClienteTienda /></RequireAuth>} />
          <Route path="/cliente/cargar-tarjeta/:clienteId" element={<RequireAuth allow={['cliente','admin']}><RequirePerfilCompleto><ClienteCargarTarjeta /></RequirePerfilCompleto></RequireAuth>} />
          <Route path="/cliente/pagar-pedido/:pedidoId" element={<RequireAuth allow={['cliente','admin']}><RequirePerfilCompleto><ClientePagarPedido /></RequirePerfilCompleto></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  )
}
